import './index.css';
import React, { useState, useMemo, useEffect, createContext } from 'react';
import {
    getTimestamps,
    useGetIDsFromURL,
    getDeviceType,
    fillMissingBuckets,
    getMissingRanges,
} from 'components/DeviceDetails/helper';
import dayjs from 'dayjs';
import {
    ViewMode,
    DeviceTypes,
    ChartLabels,
} from 'components/DeviceDetails/constants';
import { useJwtToken } from 'helper';
import { useChartData, useChartAvailability, generateStatsReport } from 'components/HGChart/service';
import { getSnKey, getLineLabels } from 'components/HGChart/helper';
import { useToast } from 'components/shadcn/hooks/use-toast';
import { useQueryClient } from 'react-query';

export const ChartContext = createContext();

ChartContext.displayName = 'ChartContext';


const ChartProvider = ({ devices, children }) => {
    const accessToken = useJwtToken();
    const {
        Thing_ID,
        BMS_ID,
        Module_ID,
        Site_ID: isSiteDetailPage,
    } = useGetIDsFromURL();
    const packId = Module_ID ?? BMS_ID;

    const findPack = (id, devs) =>
        devs.find((d) => (typeof d === 'string' ? d.trim() === id : d?.PACK_SN === id));

    const [selectedDevice, setSelectedDevice] = useState(
        () => (packId ? findPack(packId, devices) : null) ?? devices[0]
    );

    useEffect(() => {
        if (packId) {
            const match = findPack(packId, devices);
            if (match) setSelectedDevice(match);
        } else if (!selectedDevice && devices.length > 0) {
            setSelectedDevice(devices[0]);
        }
    }, [packId, devices]); // eslint-disable-line react-hooks/exhaustive-deps
    const [chartType, setChartType] = useState({
        title: ChartLabels.All,
    });
    const [selectedDate, setSelectedDate] = useState(dayjs());
    const [calendarViewMonth, setCalendarViewMonth] = useState(dayjs());
    const [viewMode, setViewMode] = useState(ViewMode.DAY); // ["day", "month", "year"]
    const [useRawData, setUseRawData] = useState(false);

    const deviceType = useMemo(
        () => getDeviceType(selectedDevice, BMS_ID, Module_ID),
        [selectedDevice, BMS_ID, Module_ID]
    );
    const timestamps = useMemo(
        () => getTimestamps(selectedDate, viewMode),
        [selectedDate, viewMode]
    );

    const serialNumberKey = useMemo(
        () =>
            getSnKey(
                Module_ID ?? BMS_ID ?? selectedDevice?.Thing_ID ?? Thing_ID,
                deviceType
            ),
        [Module_ID, BMS_ID, selectedDevice, Thing_ID, deviceType]
    );

    const {
        data: rawStats = [],
        isFetching: statsLoading,
        refetch: onRefresh,
    } = useChartData(serialNumberKey, timestamps, useRawData);

    const stats = useMemo(
        () => fillMissingBuckets(rawStats, timestamps.startTimestamp, timestamps.endTimestamp, viewMode),
        [rawStats, timestamps, viewMode]
    );

    const missingRanges = useMemo(() => getMissingRanges(stats), [stats]);

    const lineLabels = useMemo(
        () => getLineLabels(stats, deviceType, chartType),
        [stats, deviceType, chartType]
    );

    // Strip the type suffix (e.g. "ABC123_PACK" → "ABC123") to get the bare serial number
    // used as the partition key in ChartAvailability.
    const serialNumber = useMemo(
        () => serialNumberKey?.includes('_')
            ? serialNumberKey.split('_').slice(0, -1).join('_')
            : serialNumberKey,
        [serialNumberKey]
    );

    // Single lightweight fetch for all dates that have data — replaces the three
    // separate month/year/page chart-data calls. staleTime: Infinity because
    // historical availability is immutable.
    const { data: availabilityDates = [] } = useChartAvailability(serialNumber);

    const daysWithData = useMemo(
        () => availabilityDates.filter((d) => d.startsWith(calendarViewMonth.format('YYYY-MM'))),
        [availabilityDates, calendarViewMonth]
    );

    const monthsWithData = useMemo(
        () => [...new Set(availabilityDates.map((d) => d.slice(0, 7)))],
        [availabilityDates]
    );

    const yearsWithData = useMemo(
        () => [...new Set(availabilityDates.map((d) => parseInt(d.slice(0, 4), 10)))],
        [availabilityDates]
    );

    const [brushStartIndex, setBrushStartIndex] = useState(0);
    const [brushEndIndex, setBrushEndIndex] = useState(0);
    const [showResetZoom, setShowResetZoom] = useState(false);
    const [showLines, setShowLines] = useState([]); // lines to show on graph
    const [filteredLines, setFilteredLines] = useState([]); // all the lines available on graph to toggle
    const [key, setKey] = useState(0);

    useEffect(() => {
        setFilteredLines(lineLabels);
        setShowLines(lineLabels);
    }, [lineLabels, chartType, setShowLines]);

    // Keep calendar nav state in sync when the user selects a date (closes the picker)
    useEffect(() => {
        setCalendarViewMonth(selectedDate);
    }, [selectedDate]);

    // Reset Zoom Function
    const resetZoom = () => {
        setKey(key + 1);
        setBrushStartIndex(0);
        setBrushEndIndex(0);
        setShowResetZoom(false);
    };

    // Report Generation
    const { toast } = useToast();
    const queryClient = useQueryClient();

    const generateReport = async () => {
        const generationToast = toast({
            title: 'Report Generating...',
            description: 'You can find it in the Reports tab when complete.',
            duration: 600000,
            variant: 'info',
        });
        const { startTimestamp, endTimestamp } = timestamps;
        try {
            const report = await generateStatsReport(accessToken, {
                Thing_ID: selectedDevice?.Thing_ID ?? Thing_ID,
                Report_View: viewMode,
                Start_Date: startTimestamp,
                End_Date: endTimestamp,
            });
            if (report) {
                queryClient
                    .invalidateQueries([
                        'reports',
                        selectedDevice?.Thing_ID ?? Thing_ID,
                    ])
                    .then(() => {
                        generationToast.dismiss();
                        toast({
                            title: 'Report Available',
                            description:
                                'Your report has been generated, you can find it in the Reports tab.',
                            duration: 3500,
                            variant: 'success',
                        });
                    });
            }
        } catch (error) {
            console.error('Error generating report:', error);
            generationToast.dismiss();
            toast({
                title: 'Report Generation Failed',
                description:
                    'There was an error generating your report. Please try again.',
                duration: 3500,
                variant: 'destructive',
            });
        }
    };

    return (
        <ChartContext.Provider
            value={{
                stats,
                statsLoading,
                selectedDate,
                setSelectedDate,
                viewMode,
                setViewMode,
                deviceType: isSiteDetailPage
                    ? DeviceTypes.EMS
                    : deviceType === DeviceTypes.MODULE
                      ? DeviceTypes.Pack
                      : deviceType,
                devices,
                selectedDevice,
                setSelectedDevice,
                timestamps,
                lineLabels,
                chartType,
                setChartType,
                brushStartIndex,
                setBrushStartIndex,
                brushEndIndex,
                setBrushEndIndex,
                key,
                setKey,
                filteredLines,
                showLines,
                setShowLines,
                useRawData,
                setUseRawData,
                onRefresh,
                resetZoom,
                showResetZoom,
                setShowResetZoom,
                isSiteDetailPage,
                generateReport,
                daysWithData,
                setCalendarViewMonth,
                monthsWithData,
                yearsWithData,
                missingRanges,
            }}
        >
            {children}
        </ChartContext.Provider>
    );
};

export default ChartProvider;
